/**
 * Base Color Primitives
 * These are raw color values that semantic colors reference
 */

export const baseColors = {
  // Primitives
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',

  // Neutral scale (for backgrounds, borders, text)
  neutral: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },

  // Color families (brand colors, status colors)
  green: {
    50: '#E8F5E9',
    100: '#C8E6C9',
    200: '#A5D6A7',
    300: '#81C784',
    400: '#66BB6A',
    500: '#109a2e', // Primary green
    600: '#43A047',
    700: '#388E3C',
    800: '#2E7D32',
    900: '#1B5E20',
  },

  red: {
    50: '#FFEBEE',
    100: '#FFCDD2',
    200: '#EF9A9A',
    300: '#E57373',
    400: '#EF5350',
    500: '#E30A18', // Primary red
    600: '#CD5C5C',
    700: '#D32F2F',
    800: '#C62828',
    900: '#B71C1C',
  },

  yellow: {
    50: '#FFFDE7',
    100: '#FFF9C4',
    200: '#FFF59D',
    300: '#FFF176',
    400: '#FFEE58',
    500: '#ffee58', // Primary yellow/info
    600: '#FDD835',
    700: '#FBC02D',
    800: '#F9A825',
    900: '#F57F17',
  },

  orange: {
    50: '#FFF3E0',
    100: '#FFE0B2',
    200: '#FFCC80',
    300: '#FFB74D',
    400: '#FFA726',
    500: '#FF9800',
    600: '#FB8C00',
    700: '#F57C00',
    800: '#EF6C00',
    900: '#E65100',
  },

  blue: {
    50: '#E3F2FD',
    100: '#BBDEFB',
    200: '#90CAF9',
    300: '#64B5F6',
    400: '#42A5F5',
    500: '#2196F3',
    600: '#1E88E5',
    700: '#1976D2',
    800: '#1565C0',
    900: '#0D47A1',
  },

  gray: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#505050', // Secondary gray
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },

  // Atlas Design System palette (Sistema Atlas)
  atlas: {
    // Primary green scale
    primary700: '#1F4D45',     // Header
    primary600: '#256158',     // Hover
    primary500: '#2F7A6D',     // Buttons / Active
    primary100: '#E7F3F0',     // Background soft

    // Accent yellow scale
    accent500: '#E5B100',      // Primary accent
    accent400: '#F2C94C',      // Hover
    accent100: '#FFF6D6',      // Soft background

    // Neutrals
    background: '#F5F7F6',     // Page background
    cardBg: '#FFFFFF',         // Card background
    border: '#E3E6E5',         // Borders soft
    textPrimary: '#2B2F2E',    // Text primary
    textSecondary: '#6d746bff',  // Text secondary

    // States
    error: '#D64545',          // Error
    neutralBadge: '#DDE3E1',   // Neutral badge

    // Legacy mappings (for backwards compat during transition)
    forestGreen: '#1F4D45',
    gold: '#E5B100',
    accentGreen: '#2F7A6D',
    grayBg: '#F0F3F2',
  },

  // Chart colors (Periodos P1-P6)
  chart: {
    p1: '#5B8F87',
    p2: '#7FB7AD',
    p3: '#B8D9D3',
    p4: '#E5B100',
    p5: '#D9B24C',
    p6: '#C6C6C6',
    grid: '#EEF1F0',
    trendLine: '#2F7A6D',      // Primary 500
  },

  // Table colors
  table: {
    headerBg: '#F0F3F2',
    rowAlt: '#FAFBFA',
    rowHover: '#F2F6F5',
    border: '#E3E6E5',
  },
} as const;

export type BaseColors = typeof baseColors;
